import { Card, Group, Stack, Switch, Text, ThemeIcon } from "@mantine/core";
import { NumberInput, TextInput } from "react-hook-form-mantine";
import { FEATURE_PACKAGES, FEATURE_FLAGS_METADATA } from "@congrs/schemas";
import { getFeatureStyle } from "./featureStyles";

function PackageFieldRow({ field, control, disabled }) {
  const style = getFeatureStyle(field.name);
  const Icon = style.icon;

  const inputProps = {
    name: field.name,
    control,
    disabled,
    size: "sm",
    placeholder: field.placeholder,
  };

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      py="xs"
      px="sm"
      style={{ borderRadius: "var(--mantine-radius-sm)" }}
    >
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon size="md" radius="sm" variant="light" color={style.color}>
          <Icon size={14} />
        </ThemeIcon>
        <Text size="sm">{field.label}</Text>
      </Group>
      {field.type === "number" ? (
        <NumberInput
          {...inputProps}
          step={field.step}
          allowDecimal={false}
          allowNegative={false}
        />
      ) : (
        <TextInput {...inputProps} />
      )}
    </Group>
  );
}

function FeatureRow({ flag, checked, disabled, onToggle }) {
  const meta = FEATURE_FLAGS_METADATA[flag];
  const style = getFeatureStyle(flag);
  const Icon = style.icon;

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      py="xs"
      px="sm"
      style={{ borderRadius: "var(--mantine-radius-sm)" }}
    >
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon size="md" radius="sm" variant="light" color={style.color}>
          <Icon size={14} />
        </ThemeIcon>
        <Text size="sm">{meta?.label || flag}</Text>
      </Group>
      <Switch
        size="sm"
        checked={checked}
        disabled={disabled}
        color={style.color}
        onChange={(e) => onToggle(e.currentTarget.checked)}
      />
    </Group>
  );
}

export default function FeatureFlagsSelector({
  value = [],
  onChange,
  control,
  disabled = false,
}) {
  const features = value || [];

  const toggleFlag = (flag, checked) => {
    if (checked) {
      onChange([...features, flag]);
    } else {
      onChange(features.filter((f) => f !== flag));
    }
  };

  const togglePackage = (pkg, checked) => {
    if (checked) {
      onChange([...new Set([...features, ...pkg.flags])]);
    } else {
      onChange(features.filter((f) => !pkg.flags.includes(f)));
    }
  };

  return (
    <Stack mt="md" gap="sm">
      {Object.values(FEATURE_PACKAGES).map((pkg) => {
        const allSelected = pkg.flags.every((flag) => features.includes(flag));
        const packageColor = getFeatureStyle(pkg.flags[0]).color;

        return (
          <Card key={pkg.key} withBorder radius="md" padding={0}>
            <Group
              justify="space-between"
              wrap="nowrap"
              p="sm"
              bg="gray.0"
              style={{
                borderBottom: "1px solid var(--mantine-color-gray-2)",
              }}
            >
              <div style={{ width: "75%" }}>
                <Text size="sm" fw={600}>
                  {pkg.label}
                </Text>
                <Text size="xs" c="dimmed" lh={1.4}>
                  {pkg.description}
                </Text>
              </div>
              {pkg.flags.length > 0 && (
                <Switch
                  size="sm"
                  checked={allSelected}
                  disabled={disabled}
                  color={packageColor}
                  label={allSelected ? "All on" : "All off"}
                  labelPosition="left"
                  styles={{
                    label: { fontSize: "var(--mantine-font-size-xs)" },
                  }}
                  onChange={(e) => togglePackage(pkg, e.currentTarget.checked)}
                />
              )}
            </Group>

            <Stack gap={0} p="xs">
              {pkg.flags.map((flag) => (
                <FeatureRow
                  key={flag}
                  flag={flag}
                  checked={features.includes(flag)}
                  disabled={disabled}
                  onToggle={(checked) => toggleFlag(flag, checked)}
                />
              ))}
              {pkg.fields?.map((field) => (
                <PackageFieldRow
                  key={field.name}
                  field={field}
                  control={control}
                  disabled={disabled}
                />
              ))}
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
}
